function pad(value: number) {
  return String(value).padStart(2, "0");
}

function formatFromDate(date: Date) {
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} - ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

function formatDateOnly(date: Date) {
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
}

function formatDayMonth(date: Date) {
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}`;
}

function parseLocalSqlDate(value: string) {
  const match = value.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (!match) return null;
  const [, year, month, day, hour = "00", minute = "00", second = "00"] = match;
  const date = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second)
  );
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

export function parseDateTimeValue(value?: string | null) {
  const raw = String(value || "").trim();
  if (!raw) return null;

  const isoLike = raw.includes("T") && (raw.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(raw));
  if (isoLike) {
    const date = new Date(raw);
    if (!Number.isNaN(date.getTime())) return date;
  }

  const localSqlDate = parseLocalSqlDate(raw);
  if (localSqlDate) return localSqlDate;

  const fallback = new Date(raw);
  if (!Number.isNaN(fallback.getTime())) return fallback;
  return null;
}

export function formatDateTimeLabel(value?: string | null) {
  if (!value) return "-";
  const raw = String(value).trim();
  if (!raw) return "-";

  const parsed = parseDateTimeValue(raw);
  if (parsed) return formatFromDate(parsed);
  return raw;
}

export function formatTimeLabel(value?: string | null) {
  const parsed = parseDateTimeValue(value);
  if (!parsed) return "-";
  return `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
}

export function formatDateLabel(value?: string | null) {
  const parsed = parseDateTimeValue(value);
  if (!parsed) return "-";
  return formatDateOnly(parsed);
}

function isSameDay(start: Date, end: Date) {
  return (
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate()
  );
}

export function formatEventDateRangeLabel(
  startValue?: string | null,
  endValue?: string | null,
) {
  const startRaw = String(startValue || "").trim();
  const endRaw = String(endValue || "").trim();
  if (!startRaw && !endRaw) return "";

  const start = parseDateTimeValue(startRaw);
  const end = parseDateTimeValue(endRaw);

  if (!start && !end) return startRaw || endRaw;
  if (!start) return formatDateTimeLabel(endRaw);
  if (!end || isSameDay(start, end)) return formatDateTimeLabel(startRaw);

  const sameYear = start.getFullYear() === end.getFullYear();
  const sameMonth = sameYear && start.getMonth() === end.getMonth();

  if (sameMonth) {
    return `${pad(start.getDate())} a ${formatDateOnly(end)}`;
  }

  if (sameYear) {
    return `${formatDayMonth(start)} a ${formatDateOnly(end)}`;
  }

  return `${formatDateOnly(start)} a ${formatDateOnly(end)}`;
}

export function formatEventDateOnlyRangeLabel(
  startValue?: string | null,
  endValue?: string | null,
) {
  const startRaw = String(startValue || "").trim();
  const endRaw = String(endValue || "").trim();
  if (!startRaw && !endRaw) return "";

  const start = parseDateTimeValue(startRaw);
  const end = parseDateTimeValue(endRaw);

  if (!start && !end) return startRaw || endRaw;
  if (!start) return formatDateLabel(endRaw);
  if (!end || isSameDay(start, end)) return formatDateOnly(start);

  const sameYear = start.getFullYear() === end.getFullYear();
  const sameMonth = sameYear && start.getMonth() === end.getMonth();

  if (sameMonth) {
    return `${pad(start.getDate())} a ${formatDateOnly(end)}`;
  }

  if (sameYear) {
    return `${formatDayMonth(start)} a ${formatDateOnly(end)}`;
  }

  return `${formatDateOnly(start)} a ${formatDateOnly(end)}`;
}
